import { applyConditionStageUpdate } from '../combatStageConditions';

const mergeSpellSnapshotContainer = (baseContainer, incomingContainer, updatedSpells, depth = 0) => {
  if (!baseContainer && !incomingContainer && !updatedSpells) {
    return null;
  }

  const mergedContainer = {
    ...(baseContainer && typeof baseContainer === 'object' ? baseContainer : {}),
    ...(incomingContainer && typeof incomingContainer === 'object' ? incomingContainer : {})
  };

  if (updatedSpells && typeof updatedSpells === 'object') {
    // date_of_change: 2026-06-07
    // WHAT: Mirror spent spell slots into every canonical spell container on the token snapshot.
    // WHY: BattleRoom selectors can read root spells, characterData.spells, canonicalData.spells,
    // or data.spellcasting. Updating only one path made the right panel and action catalog disagree
    // after a combat spell cast.
    mergedContainer.spells = {
      ...(baseContainer?.spells && typeof baseContainer.spells === 'object' ? baseContainer.spells : {}),
      ...(incomingContainer?.spells && typeof incomingContainer.spells === 'object' ? incomingContainer.spells : {}),
      ...updatedSpells
    };

    if (
      baseContainer?.spellcasting
      || incomingContainer?.spellcasting
      || updatedSpells.slots
    ) {
      mergedContainer.spellcasting = {
        ...(baseContainer?.spellcasting && typeof baseContainer.spellcasting === 'object' ? baseContainer.spellcasting : {}),
        ...(incomingContainer?.spellcasting && typeof incomingContainer.spellcasting === 'object' ? incomingContainer.spellcasting : {})
      };
      if (updatedSpells.slots) {
        mergedContainer.spellcasting.slots = updatedSpells.slots;
      }
    }

    if (baseContainer?.data || incomingContainer?.data) {
      mergedContainer.data = {
        ...(baseContainer?.data && typeof baseContainer.data === 'object' ? baseContainer.data : {}),
        ...(incomingContainer?.data && typeof incomingContainer.data === 'object' ? incomingContainer.data : {})
      };
      mergedContainer.data.spells = {
        ...(baseContainer?.data?.spells && typeof baseContainer.data.spells === 'object' ? baseContainer.data.spells : {}),
        ...(incomingContainer?.data?.spells && typeof incomingContainer.data.spells === 'object' ? incomingContainer.data.spells : {}),
        ...updatedSpells
      };
      if (
        baseContainer?.data?.spellcasting
        || incomingContainer?.data?.spellcasting
        || updatedSpells.slots
      ) {
        mergedContainer.data.spellcasting = {
          ...(baseContainer?.data?.spellcasting && typeof baseContainer.data.spellcasting === 'object' ? baseContainer.data.spellcasting : {}),
          ...(incomingContainer?.data?.spellcasting && typeof incomingContainer.data.spellcasting === 'object' ? incomingContainer.data.spellcasting : {})
        };
        if (updatedSpells.slots) {
          mergedContainer.data.spellcasting.slots = updatedSpells.slots;
        }
      }
    }

    if (depth < 4 && (baseContainer?.canonicalData || incomingContainer?.canonicalData)) {
      // date_of_change: 2026-08-20
      // WHAT: Carry the spell snapshot into nested canonicalData containers as well.
      // WHY: Default character tokens retain their original canonical payload inside
      // characterData.canonicalData, and sheet selectors may prefer that path after a cast.
      mergedContainer.canonicalData = mergeSpellSnapshotContainer(
        baseContainer?.canonicalData,
        incomingContainer?.canonicalData,
        updatedSpells,
        depth + 1
      );
    }

    if (depth < 4) {
      ['characterData', 'entityPayload', 'schemaData', 'originalUnifiedEntity', 'runtimeOverlay', 'visualState'].forEach((key) => {
        if (!baseContainer?.[key] && !incomingContainer?.[key]) return;
        mergedContainer[key] = mergeSpellSnapshotContainer(
          baseContainer?.[key],
          incomingContainer?.[key],
          updatedSpells,
          depth + 1
        );
      });
    }
  }

  return mergedContainer;
};

export const buildMergedCombatStageUpdate = (token, stageUpdate) => {
  if (!token || !stageUpdate || typeof stageUpdate !== 'object') {
    return null;
  }

  const mergedUpdate = {
    ...stageUpdate
  };

  if (stageUpdate.combatFlags && typeof stageUpdate.combatFlags === 'object') {
    mergedUpdate.combatFlags = {
      ...(token.combatFlags || {}),
      ...stageUpdate.combatFlags
    };
  }

  if (stageUpdate.statusEffects && typeof stageUpdate.statusEffects === 'object') {
    mergedUpdate.statusEffects = {
      ...(token.statusEffects || {}),
      ...stageUpdate.statusEffects
    };
  }

  if (stageUpdate.statusEffectsMeta && typeof stageUpdate.statusEffectsMeta === 'object') {
    mergedUpdate.statusEffectsMeta = {
      ...(token.statusEffectsMeta || {}),
      ...stageUpdate.statusEffectsMeta
    };
  }

  if (stageUpdate.spells && typeof stageUpdate.spells === 'object') {
    mergedUpdate.spells = {
      ...(token.spells && typeof token.spells === 'object' ? token.spells : {}),
      ...stageUpdate.spells
    };

    if (token.characterData || stageUpdate.characterData) {
      mergedUpdate.characterData = mergeSpellSnapshotContainer(token.characterData, stageUpdate.characterData, mergedUpdate.spells);
    }
    if (token.canonicalData || stageUpdate.canonicalData) {
      mergedUpdate.canonicalData = mergeSpellSnapshotContainer(token.canonicalData, stageUpdate.canonicalData, mergedUpdate.spells);
    }
    if (token.entityPayload || stageUpdate.entityPayload) {
      mergedUpdate.entityPayload = mergeSpellSnapshotContainer(token.entityPayload, stageUpdate.entityPayload, mergedUpdate.spells);
    }
    if (token.data || stageUpdate.data) {
      mergedUpdate.data = mergeSpellSnapshotContainer(token.data, stageUpdate.data, mergedUpdate.spells);
    }
    if (token.schemaData || stageUpdate.schemaData) {
      mergedUpdate.schemaData = mergeSpellSnapshotContainer(token.schemaData, stageUpdate.schemaData, mergedUpdate.spells);
    }
    if (token.originalUnifiedEntity || stageUpdate.originalUnifiedEntity) {
      mergedUpdate.originalUnifiedEntity = mergeSpellSnapshotContainer(token.originalUnifiedEntity, stageUpdate.originalUnifiedEntity, mergedUpdate.spells);
    }
    if (token.runtimeOverlay || stageUpdate.runtimeOverlay) {
      mergedUpdate.runtimeOverlay = mergeSpellSnapshotContainer(token.runtimeOverlay, stageUpdate.runtimeOverlay, mergedUpdate.spells);
    }
    if (token.visualState || stageUpdate.visualState) {
      mergedUpdate.visualState = mergeSpellSnapshotContainer(token.visualState, stageUpdate.visualState, mergedUpdate.spells);
    }
  }

  return applyConditionStageUpdate(token, stageUpdate, mergedUpdate);
};

export default buildMergedCombatStageUpdate;
